<template>
  <div
    v-if="productCode && productCode.length && (!isPdp || hasRefLinks || hasSpecItems)"
    qaid="tech-info-container"
    class="grid-container text-md"
  >
    <section>
      <div class="section-header section-header--xl section-header--dividing my-4">
        <h1 qaid="tech__parts-header" class="heading">
          <span v-if="productIsBike">{{ getBaseProperty('bikeTech') }}</span>
          <span v-else>{{ getBaseProperty('partsAccessoriesTech') }}</span>
        </h1>
      </div>
    </section>

    <!-- Product section -->
    <section>
      <div class="section-header section-header--md">
        <h2 qaid="tech__product-name-header" class="heading">{{ productModelYear }} {{ productName }}</h2>
      </div>
      <div class="grid-x grid-margin-x my-3 section-header--dividing">
        <div class="cell medium-6 large-4 mb-3">
          <div qaid="tech__product-code" class="font-medium text-gray-40 mb-1">
            {{ getBaseProperty('model') }} {{ productCode }}
          </div>
          <img :src="productImage" />
        </div>

        <div class="cell medium-6 large-8">
          <div class="grid-x grid-margin-x">
            <div v-if="references && references.length" qaid="tech__references-link" class="cell large-6">
              <div class="font-medium text-gray-40 mb-1">{{ getBaseProperty('references') }}</div>

              <ul class="mb-2">
                <li v-for="(reference, index) in references" :key="index" class="icon-item mb-1">
                  <icon class name="description"></icon>
                  <ref-link
                    class="underline"
                    :title="reference.title"
                    :link="reference.link"
                    :asset-type="reference.assetType"
                    :asset-location="reference.assetLocation"
                    :target="reference.target"
                  />
                </li>
              </ul>
            </div>

            <div class="cell large-6">
              <div v-if="videos && videos.length">
                <div class="font-medium text-gray-40 mb-1">
                  {{ getBaseProperty('videos') }}
                </div>
                <ul class="mb-2">
                  <li v-for="(video, index) in videos" :key="index" class="icon-item mb-1">
                    <icon class name="play_circle_filled"></icon>
                    <ref-link
                      class="underline"
                      :title="video.title"
                      :link="video.link"
                      :asset-type="video.assetType"
                      :asset-location="video.assetLocation"
                      :target="reference.target"
                    />
                  </li>
                </ul>
              </div>

              <div v-if="!isPdp">
                <div class="font-medium text-gray-40 mb-1">
                  {{ getBaseProperty('url') }}
                </div>
                <ul class="mb-2">
                  <li qaid="tech__product-page-link" class="icon-item mb-1">
                    <icon class name="arrow_forward"></icon>
                    <a :href="`${linkBasePath}/p/${productCode}/`" class="underline">{{
                      getBaseProperty('visitProductPage')
                    }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Replacement parts section -->
    <section v-if="hasSpecItems" qaid="tech__related-parts-section">
      <div class="grid-x grid-margin-x my-5">
        <div class="cell large-3">
          <div class="section-header section-header--md">
            <h2 class="heading">
              {{ getBaseProperty('findParts') }}
            </h2>
          </div>
          <div class="mb-1">
            {{ getBaseProperty('selectCategory') }}
          </div>
          <div class="mb-2">
            <fieldset>
              <tech-info-variant
                :variant-index="variantIndex"
                :variants="variants"
                @sku-selected="skuSelected = $event"
              />
              <div class="buttons">
                <trek-button primary block @click="findParts">
                  <span v-html="getBaseProperty('findParts')" />
                </trek-button>
                <trek-button
                  v-show="showResults"
                  secondary
                  block
                  icon="list"
                  class="hide-for-large"
                  data-toggle="b2b-off-canvas-facet"
                >
                  <span v-html="`${getBaseProperty('filterParts')} ${filterCount}`" />
                </trek-button>
              </div>
            </fieldset>
          </div>

          <div
            v-show="showResults"
            id="b2b-off-canvas-facet"
            class="b2b-techinfo-filters off-canvas position-top reveal-for-large lg:mt-4"
            data-off-canvas
            data-transition="overlap"
          >
            <div class="section-header section-header--dividing show-for-large">
              <div class="heading font-body font-medium">{{ getBaseProperty('filters') }} {{ filterCount }}</div>
            </div>
            <button
              type="button"
              class="button button--primary button--hollow button--skinny button--expanded hide-for-large mt-2"
              data-close
            >
              <div class="flex content-center justify-between w-full">
                <div class="flex items-center justify-center w-full">
                  <i class="material-icons mr-1">list</i>
                  {{ getBaseProperty('filters') }} {{ filterCount }}
                </div>
                <div class="flex-grow-0">
                  <i class="material-icons">close</i>
                </div>
              </div>
            </button>
            <trek-button v-show="filterCount" primary block class="hide-for-large" data-close>
              <span v-html="getBaseProperty('done')" />
            </trek-button>
            <collapse
              icon-active="keyboard_arrow_up"
              icon-inactive="keyboard_arrow_down"
              icon-position="right"
              :value="['filter-Parts']"
            >
              <filter-group
                v-for="(filter, index) in filterGroups"
                :key="index"
                :items="filter.items"
                :group-name="filter.name"
                :base-properties="baseProperties"
                @change="filterUpdate($event)"
              />
            </collapse>
          </div>
        </div>

        <div class="cell large-9">
          <div v-if="!showResults">
            <Callout type="secondary" icon="info">{{ getBaseProperty('selectCategory') }}</Callout>
          </div>

          <div v-if="showResults">
            <div class="flex justify-between mb-1 font-medium">
              <span>{{ filteredItems.length }} {{ getBaseProperty('results') }}</span>
              <span class="text-gray-60">{{ getBaseProperty('sku') }} {{ skuSelected }}</span>
            </div>

            <div v-for="(item, index) in displayedItems" :key="index" class="bg-gray-05 p-3 md:pb-2 mb-1">
              <dl class="description-list grid-x grid-margin-x">
                <dt qaid="tech-info-item-type" class="cell medium-3">{{ getBaseProperty('itemType') }}</dt>
                <dd class="cell medium-9">{{ item.partName }}</dd>

                <template v-if="item.description">
                  <dt class="cell medium-3">
                    {{ getBaseProperty('description') }}
                  </dt>
                  <dd class="cell medium-9">
                    {{ item.description }}
                  </dd>
                </template>

                <template v-if="item.originalItem.length > 0 || item.serviceMessage">
                  <dt class="cell medium-3">
                    {{ getBaseProperty('originalItem') }}
                  </dt>

                  <dd class="cell medium-9">
                    <a
                      v-if="item.originalItem.length > 0"
                      :href="`${contextPath}${item.originalItem[0].url}`"
                      class="underline"
                      >{{ item.originalItem[0].sku }}</a
                    >
                    <span v-else>{{ item.serviceMessage }}</span>
                  </dd>
                </template>

                <template v-if="item.alternativeItems.length > 0 || item.serviceMessage">
                  <template v-if="item.alternativeItems.length > 0">
                    <template v-for="(altItem, altItemIndex) in item.alternativeItems">
                      <dt :key="`label-${altItemIndex}`" class="cell medium-3">
                        <span v-if="altItemIndex === 0">{{ getBaseProperty('alternativeItems') }}</span>
                      </dt>
                      <dd :key="`url-${altItemIndex}`" class="cell medium-9">
                        <a :href="`${contextPath}${altItem.url}`" class="underline">{{ altItem.sku }}</a>
                      </dd>
                    </template>
                  </template>

                  <template v-else>
                    <dt class="cell medium-3">
                      {{ getBaseProperty('alternativeItems') }}
                    </dt>
                    <dd class="cell medium-9">
                      {{ item.serviceMessage }}
                    </dd>
                  </template>
                </template>
              </dl>
            </div>

            <div v-if="filteredItems.length > resultsSize" class="buttons">
              <trek-button primary block @click="resultsSize += showMoreIncrement">
                <span v-html="getBaseProperty('showMore')" />
              </trek-button>
            </div>

            <div v-else-if="filteredItems.length <= resultsSize && showMoreIncrement < resultsSize" class="buttons">
              <trek-button primary block @click="resultsSize = showMoreIncrement">
                <span v-html="getBaseProperty('showFewer')" />
              </trek-button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import filter from 'lodash/filter';
import find from 'lodash/find';
import some from 'lodash/some';
import {mapMutations, mapState} from 'vuex';
import {ProductApi} from '@/api/product';
import Icon from '@/components/Icon';
import TechInfoVariant from '@/components/containers/tech-info/TechInfoVariant';
import Collapse from '@/components/Collapse';
import FilterGroup from '@/components/FilterGroup';
import Callout from '@/components/Callout';
import TrekButton from '@/components/TrekButton';
import RefLink from '@/components/RefLink';

export default {
  components: {Icon, TechInfoVariant, Collapse, FilterGroup, Callout, TrekButton, RefLink},

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    linkBasePath: {
      type: String,
      default: null,
    },
    isPdp: Boolean,
    baseProductCode: {
      type: String,
      default: null,
    },
    productIsBike: Boolean,
    baseProductImage: {
      type: String,
      default: null,
    },
    productModelYear: {
      type: String,
      default: null,
    },
    productName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      filters: [],
      specItems: [],
      filteredItems: [],
      variantIndex: 0,
      variants: {},
      skuSelected: '',
      productImage: this.baseProductImage,
      productCode: this.baseProductCode,
      contextPath: ACC.config.encodedContextPath || '',
      filterCount: '',
      resultsSize: 15,
      showMoreIncrement: 15,
      scene7Prefix: '//trek.scene7.com/is/content/TrekBicycleProducts/',
      scene7Suffix:
        '?wid=160&amp;amp;hei=120&amp;amp;fmt=png8-alpha&amp;amp;qlt=80,1&amp;amp;op_usm=0,0,0,0&amp;amp;iccEmbed=0&amp;amp;cache=on,on',
      hasRefLinks: false,
      hasSpecItems: false,
    };
  },

  computed: {
    displayedItems() {
      return this.filteredItems.slice(0, this.resultsSize);
    },
    matrix() {
      return this.getObjectFromElement('matrix-json');
    },
    showResults() {
      return this.specItems && this.specItems.length;
    },

    filterGroups() {
      return this.filters.map((filterGroup) => {
        return {
          name: filterGroup.filterName,
          items: filterGroup.filters.map((filter) => {
            return {
              id: filter.id,
              text: filter.specItemName,
            };
          }),
        };
      });
    },

    ...mapState('storage', ['bikeTechSKU']),
  },

  watch: {
    skuSelected() {
      this.findParts();
    },
  },
  created() {
    if (this.productCode && this.productCode.length) {
      axios
        .get(`/product/spec/technicalData/${this.productCode}/`)
        .then(({data}) => {
          this.hasSpecItems = data.data.specItemsPresent;
          this.hasRefLinks = data.data.refLinkPresent;
          this.references = data.data.references;
          this.videos = data.data.videos;
        })
        .catch(() => {
          this.clearData();
        });
    }
  },

  mounted() {
    this.processMatrixVariants();
  },

  updated() {
    // Needed since the DOM updates with a v-if.
    // Tech Debt: Remove and replace with a Vue solution
    $(this.$el).foundation();
  },

  destroyed() {
    // Needed since foundation is re-initialized on update()
    // Tech Debt: Remove and replace with a Vue solution
    $(this.$el).foundation.destroy();
  },

  methods: {
    filterUpdate(filters) {
      if (filters && filters.length) {
        this.filteredItems = filter(this.specItems, (item) => {
          let found = false;

          filters.forEach((filter) => {
            if (item.partId === filter) {
              found = true;
            }
          });

          return found;
        });

        this.filterCount = `(${this.filteredItems.length})`;
      } else {
        this.filteredItems = this.specItems;
        this.filterCount = '';
      }
    },

    processMatrixVariants() {
      if (this.matrix && this.matrix.length) {
        this.variants = this.getVariantsLegacy(this.matrix);
      } else {
        ProductApi.loadProduct(this.baseProductCode).then((data) => {
          this.variants = this.getVariants(data.categorizationHierarchyValues, data);
        });
      }
    },

    // Get variants
    getVariants(variants, data) {
      if (variants && variants.length) {
        const attribute = data.categorizationHierarchyMetadata.find((item) => item.code === variants[0].code);
        let newVariant = {
          id: variants[0].code,
          label:
            attribute && attribute.options.displayName
              ? attribute.options.displayName
              : variants[0].options.displayName,
          isLeaf: !variants[0].children.length,
          options: variants.map((variant) => {
            return {
              label: variant.options.displayName,
              value: variant.options.productCode || variant.options.displayName,
              variants: variant.children && variant.children.length ? this.getVariants(variant.children, data) : null,
            };
          }),
        };

        this.processPreSelectedSku(newVariant);
        return newVariant;
      }
      return null;
    },

    // Recursive function to create a full tree of variants used in dropdowns
    getVariantsLegacy(variants) {
      if (variants && variants.length) {
        let newVariant = {
          id: variants[0].parentVariantCategory.code,
          label: variants[0].parentVariantCategory.name,
          isLeaf: variants[0].isLeaf,
          options: variants.map((variant) => {
            return {
              label: variant.variantValueCategory.name,
              value: variant.isLeaf ? variant.variantOption.code : variant.variantValueCategory.name,
              variants: variant.elements && variant.elements.length ? this.getVariantsLegacy(variant.elements) : null,
            };
          }),
        };

        this.processPreSelectedSku(newVariant);
        return newVariant;
      }
      return null;
    },

    // If there is a SKU preselected from the tech selector screen. Pre-select all the options
    // needed to show that sku
    processPreSelectedSku(newVariant) {
      if (this.bikeTechSKU && this.bikeTechSKU.length) {
        if (newVariant.isLeaf) {
          let optionFound = find(newVariant.options, (option) => option.value === this.bikeTechSKU);

          if (optionFound) {
            optionFound.selected = true;
          }
        } else {
          newVariant.options.forEach((option) => {
            option.selected = some(option.variants.options, (subOption) => subOption.selected);
          });
        }
      }
    },

    findParts() {
      this.retrieveSpecInfo(this.skuSelected);
    },

    getObjectFromElement(elementId) {
      const el = document.getElementById(elementId);

      if (el) {
        const elString = el.value;

        if (elString) {
          return JSON.parse(elString);
        }
      }

      return {};
    },

    retrieveSpecInfo(variantSku) {
      if (variantSku && variantSku.length) {
        axios
          .get(`/product/spec/${variantSku}/`)
          .then(({data}) => {
            if (data && data.data) {
              this.filters = data.data.filters;
              this.specItems = data.data.specItems;
              this.productImage = data.data.productImage;
              this.productCode = data.data.sku;
              this.filteredItems = this.specItems;
            } else {
              this.clearData();
            }
          })
          .catch(() => {
            this.clearData();
          });
      }
    },

    clearData() {
      this.filters = [];
      this.specItems = [];
      this.productImage = this.baseProductImage;
      this.productCode = this.baseProductCode;
    },

    ...mapMutations('storage', ['setBikeTechSKU']),
  },
};
</script>

<style lang="scss" scoped>
#b2b-off-canvas-facet {
  z-index: 16000020;
}
</style>
